import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Breadcrumb, Button, Card, Col, Form, Input, message, Row, Switch, Typography, Space } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import weworkNonChannelWelcomeService from '../services/weworkNonChannelWelcomeService';
import weworkService from '../services/weworkService';
import fileService from '../services/fileService';
import EmployeeSelect from '../components/common/EmployeeSelect';
import AttachmentManager from '../components/sop/AttachmentManager';

const { Title, Text } = Typography;

const WeworkNonChannelWelcomeEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const idRef = useRef(1);
  const generateId = useCallback(() => `ATT_${Date.now()}_${idRef.current++}`, []);

  const [attachments, setAttachments] = useState(() => [{ id: `ATT_${Date.now()}_0`, type: 'TEXT', content: '' }]);
  const [previewUrls, setPreviewUrls] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const normalizeAttachment = useCallback((att) => {
    if (!att) return null;
    const type = att.type;
    const base = { id: att.id || generateId(), type };

    if (type === 'TEXT') {
      return { ...base, content: att.content || '' };
    }
    if (type === 'IMAGE_GROUP') {
      const imageGroup = att.imageGroup || {};
      return {
        ...base,
        imageGroup: {
          mediaIds: Array.isArray(imageGroup.mediaIds) ? imageGroup.mediaIds : [],
          ossUrls: imageGroup.ossUrls && typeof imageGroup.ossUrls === 'object' ? imageGroup.ossUrls : {}
        }
      };
    }
    if (type === 'VIDEO') {
      return {
        ...base,
        mediaId: att.mediaId || '',
        ossUrl: att.ossUrl || '',
        videoTitle: att.videoTitle || '',
        videoDescription: att.videoDescription || ''
      };
    }
    if (type === 'LINK') {
      return {
        ...base,
        title: att.title || '',
        description: att.description || '',
        url: att.url || '',
        picUrl: att.picUrl || att.pic_url || ''
      };
    }
    if (type === 'MINIPROGRAM') {
      return {
        ...base,
        title: att.title || '',
        appid: att.appid || '',
        pagepath: att.pagepath || '',
        thumbMediaId: att.thumbMediaId || '',
        thumbMediaId_ossUrl: att.thumbMediaId_ossUrl || ''
      };
    }
    // 其他未知类型，直接回传并补id
    return { ...att, ...base };
  }, [generateId]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await weworkNonChannelWelcomeService.getById(id);
        if (res.success && res.data) {
          const data = res.data;
          // 加载员工列表用于将存储的企微userid转换为系统员工ID
          let allEmployees = [];
          try {
            allEmployees = await weworkService.getAllEmployees();
          } catch (e) {
            // 忽略加载失败，后续仍按原值回填
          }
          form.setFieldsValue({
            title: data.title,
            // 将存储的企微userid转换为 EmployeeSelect 需要的系统员工ID
            userIds: (() => {
              try {
                const raw = JSON.parse(data.userIds || '[]');
                if (!Array.isArray(raw) || raw.length === 0) return [];
                return raw.map(uid => {
                  const emp = (allEmployees || []).find(e => e && (e.userid === uid || String(e.id) === String(uid)));
                  return emp ? String(emp.id) : String(uid);
                });
              } catch {
                return [];
              }
            })(),
            status: typeof data.status === 'number' ? data.status : 1
          });
          const list = [];
          if (data.textContent) list.push(normalizeAttachment({ type: 'TEXT', content: data.textContent }));
          const others = Array.isArray(data.attachments) ? data.attachments : [];
          const normalized = others.map(a => normalizeAttachment(a)).filter(Boolean);
          // 若无文字项，补一个
          const hasText = list.concat(normalized).some(a => a && a.type === 'TEXT');
          const finalList = hasText ? list.concat(normalized) : [normalizeAttachment({ type: 'TEXT', content: '' })].concat(normalized);
          setAttachments(finalList);

          // 初始化预览URLs
          const initialPreview = {};
          finalList.forEach(att => {
            if (!att) return;
            if ((att.type === 'IMAGE_GROUP' || att.type === 'MOMENT_IMAGE_GROUP') && att.imageGroup && att.imageGroup.ossUrls) {
              Object.assign(initialPreview, att.imageGroup.ossUrls);
            } else if ((att.type === 'VIDEO' || att.type === 'FILE') && att.mediaId && att.ossUrl) {
              initialPreview[att.mediaId] = att.ossUrl;
            } else if (att.type === 'MINIPROGRAM' && att.thumbMediaId && att.thumbMediaId_ossUrl) {
              initialPreview[att.thumbMediaId] = att.thumbMediaId_ossUrl;
            }
          });
          setPreviewUrls(initialPreview);
        }
      } catch (e) {
        // ignore
      }
    })();
  }, [id, isEdit, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const safeList = Array.isArray(attachments) ? attachments : [];
      const text = (safeList.find(a => a && a.type === 'TEXT') || {}).content || '';
      const otherAttachments = safeList.filter(a => a && a.type !== 'TEXT');
      const payload = {
        id: isEdit ? Number(id) : undefined,
        title: values.title,
        userIds: JSON.stringify(values.userIds || []),
        textContent: text,
        attachments: otherAttachments,
        status: values.status ?? 1
      };
      const res = await weworkNonChannelWelcomeService.save(payload);
      if (res.success) {
        message.success('保存成功');
        navigate('/wework-non-channel-welcome');
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e) {
      // 校验失败
    } finally {
      setSaving(false);
    }
  };

  const safeAttachments = Array.isArray(attachments) ? attachments : [];

  const handleUpdateAttachment = useCallback((attachmentId, field, value) => {
    setAttachments(prev => {
      const list = Array.isArray(prev) ? prev : [];
      // 如果没有提供ID，尝试更新文本附件
      const targetIndex = attachmentId ? list.findIndex(a => a && a.id === attachmentId) : list.findIndex(a => a && a.type === 'TEXT');
      if (targetIndex === -1) return list;
      const target = list[targetIndex] || {};
      const updated = field === 'imageGroup' ? { ...target, imageGroup: value } : { ...target, [field]: value };
      const next = list.slice();
      next[targetIndex] = updated;
      return next;
    });
  }, []);

  // 插入客户昵称
  const insertNickname = () => {
    setAttachments(prev => {
      const list = Array.isArray(prev) ? prev.slice() : [];
      const idx = list.findIndex(a => a && a.type === 'TEXT');
      if (idx === -1) return [{ id: generateId(), type: 'TEXT', content: '%NICKNAME%' }, ...list];
      const target = list[idx];
      const before = target.content || '';
      list[idx] = { ...target, content: `${before}%NICKNAME%` };
      return list;
    });
  };

  const handleAddAttachment = useCallback((type) => {
    if (!type) return undefined;
    const createByType = (t) => {
      const id = generateId();
      if (t === 'TEXT') return { id, type: t, content: '' };
      if (t === 'IMAGE_GROUP') return { id, type: t, imageGroup: { mediaIds: [], ossUrls: {} } };
      if (t === 'VIDEO') return { id, type: t, mediaId: '', ossUrl: '', videoTitle: '', videoDescription: '' };
      if (t === 'LINK') return { id, type: t, title: '', description: '', url: '' };
      if (t === 'MINIPROGRAM') return { id, type: t, title: '', appid: '', pagepath: '', thumbMediaId: '', thumbMediaId_ossUrl: '' };
      return { id, type: t };
    };
    const newItem = createByType(type);
    setAttachments(prev => (Array.isArray(prev) ? prev : []).concat(newItem));
    return newItem.id;
  }, [generateId]);

  const handleRemoveAttachment = useCallback((attachmentId) => {
    setAttachments(prev => {
      const base = Array.isArray(prev) ? prev : [];
      return base.filter(x => x && x.id !== attachmentId);
    });

    // 清理相关预览URLs
    setPreviewUrls(prev => {
      const next = { ...prev };
      const target = (Array.isArray(attachments) ? attachments : []).find(x => x && x.id === attachmentId);
      if (target) {
        if ((target.type === 'VIDEO' || target.type === 'FILE') && target.mediaId) {
          delete next[target.mediaId];
        }
        if ((target.type === 'IMAGE_GROUP' || target.type === 'MOMENT_IMAGE_GROUP') && target.imageGroup && Array.isArray(target.imageGroup.mediaIds)) {
          target.imageGroup.mediaIds.forEach(mid => delete next[mid]);
        }
        if (target.type === 'MINIPROGRAM' && target.thumbMediaId) {
          delete next[target.thumbMediaId];
        }
      }
      return next;
    });
  }, []);

  // 文件上传
  const handleFileUpload = useCallback(async ({ file, onSuccess, onError, onProgress, fieldKey, mediaType, stepType = 'MESSAGE' }) => {
    try {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: true, [fieldKey]: true }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0, [fieldKey]: 0 }));

      const updateProgress = (percent) => {
        setUploadProgress(prev => ({ ...prev, [uploadKey]: percent, [fieldKey]: percent }));
        if (onProgress) onProgress({ percent });
      };

      // 获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      if (!tenantStr) {
        message.error('未获取到租户信息，请重新登录');
        if (onError) onError(new Error('未获取到租户信息'));
        return;
      }
      const tenant = JSON.parse(tenantStr);
      const tenantId = tenant.id;

      // 使用临时素材上传（消息场景）
      const result = await fileService.uploadMedia(file, mediaType, tenantId, 'message', updateProgress);

      if (result?.success && result.mediaId) {
        setPreviewUrls(prev => ({ ...prev, [result.mediaId]: result.ossUrl }));
        message.success(`${file.name} 上传成功`);
        if (onSuccess) onSuccess(result);
        return result;
      } else {
        const errorMsg = result?.message || '上传失败';
        message.error(errorMsg);
        if (onError) onError(new Error(errorMsg));
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      message.error(`${file?.name || '文件'} 上传失败: ${error.message}`);
      if (onError) onError(error);
    } finally {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: false, [fieldKey]: false }));
    }
  }, []);

  // 文件移除（主要用于视频、小程序缩略图等）
  const handleRemoveFile = useCallback((fieldKey, stepType = 'MESSAGE') => {
    const uploadKey = `${stepType}_${fieldKey}`;
    setUploading(prev => ({ ...prev, [uploadKey]: false, [fieldKey]: false }));
    setUploadProgress(prev => ({ ...prev, [uploadKey]: 0, [fieldKey]: 0 }));

    // 清理预览，根据attachmentId匹配
    const target = (Array.isArray(attachments) ? attachments : []).find(att => att && att.id === fieldKey);
    if (target && target.mediaId) {
      setPreviewUrls(prev => {
        const next = { ...prev };
        delete next[target.mediaId];
        return next;
      });
    }
  }, [attachments]);

  // 批量更新预览URL（供AttachmentManager内部快捷更新）
  const handleUpdatePreviewUrls = useCallback((map) => {
    if (!map || typeof map !== 'object') return;
    setPreviewUrls(prev => ({ ...prev, ...map }));
  }, []);

  return (
    <div className="wework-contact-way-page">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Breadcrumb>
            <Breadcrumb.Item onClick={() => navigate('/wework-non-channel-welcome')}>
              <Title level={5} style={{ cursor: 'pointer', color: '#1677ff', margin: 0 }}>成员欢迎语</Title>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Title level={5} style={{ margin: 0 }}>{isEdit ? '编辑' : '新建'}</Title>
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
      </Row>

      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Title level={5} style={{ marginBottom: 24 }}>基础信息</Title>
          <Form.Item name="title" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="请输入名称" style={{ width: '40%' }} />
          </Form.Item>
          <Form.Item name="userIds" label="生效成员">
            <EmployeeSelect multiple style={{ width: '40%' }} />
          </Form.Item>

          <Title level={5} style={{ margin: '16px 0 24px' }}>
            欢迎语内容
            <Button
              size="small"
              onClick={insertNickname}
              style={{ marginLeft: '12px' }}
            >
              插入客户昵称 (%NICKNAME%)
            </Button>
          </Title>
          <AttachmentManager
            attachments={safeAttachments}
            onAddAttachment={handleAddAttachment}
            onRemoveAttachment={handleRemoveAttachment}
            onUpdateContent={handleUpdateAttachment}
            previewUrls={previewUrls}
            uploading={uploading}
            uploadProgress={uploadProgress}
            handleFileUpload={handleFileUpload}
            handleRemoveFile={handleRemoveFile}
            handleUpdatePreviewUrls={handleUpdatePreviewUrls}
            stepType="MESSAGE"
            isTemplateActive={false}
          />

          <Title level={5} style={{ margin: '16px 0 8px' }}>状态</Title>
          <Form.Item shouldUpdate>
            {() => {
              const current = form.getFieldsValue();
              const checked = (current.status ?? 1) === 1;
              return (
                <Switch
                  checkedChildren="启用"
                  unCheckedChildren="禁用"
                  checked={checked}
                  onChange={(v) => form.setFieldsValue({ status: v ? 1 : 0 })}
                />
              );
            }}
          </Form.Item>

          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>保存</Button>
              <Button onClick={() => navigate('/wework-non-channel-welcome')}>取消</Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default WeworkNonChannelWelcomeEdit;


